import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, ChevronDown } from 'lucide-react';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useLocalStorage } from 'react-use';
import { useNavigate } from 'react-router-dom';
import { fetchMonitoringDashboard } from '../../services/dashboardServices';
import { AUTHORIZED_NIK } from '../../helpers/currentUser';
import {
  findFirstAllowedDashboardIndex,
  getAllowedDashboardIds,
  isDashboardAllowed,
} from '../../helpers/dashboardAccess';

const EXPANDED_WIDTH = 220;
const COLLAPSED_WIDTH = 62;

const Sidebar = ({ collapsed, onClose, user = {} }) => {
  const location = useLocation();
  const [, setToken] = useLocalStorage('token', '');
  const [displayAllOpen, setDisplayAllOpen] = useState(true);
  const [dashboardSource, setDashboardSource] = useState([]);
  const navigate = useNavigate();

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && path !== '#' && location.pathname.startsWith(path))
      return true;
    return false;
  };

  const NavItem = ({ icon, label, to, tooltip }) => {
    const active = isActive(to);
    const [showTooltip, setShowTooltip] = useState(false);
    return (
      <div style={{ position: 'relative' }}>
        <Link
          to={to}
          onClick={onClose}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '9px 14px',
            marginBottom: '1px',
            borderRadius: '8px',
            textDecoration: 'none',
            fontSize: '13px',
            fontWeight: active ? '600' : '400',
            color: active ? '#a94007' : '#3f3f3f',
            background: active ? '#fff7ed' : 'transparent',
            borderLeft: active ? '3px solid #a94007' : '3px solid transparent',
            transition: 'background 0.15s',
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}
          onMouseEnter={(e) => {
            if (!active) e.currentTarget.style.background = '#fff7ed';
          }}
          onMouseLeave={(e) => {
            if (!active) e.currentTarget.style.background = 'transparent';
          }}
        >
          <i
            className={icon}
            style={{
              fontSize: '14px',
              width: '18px',
              textAlign: 'center',
              opacity: active ? 1 : 0.6,
              color: active ? '#f97316' : '#000000',
            }}
          />
          {!collapsed && <span>{label}</span>}
          {tooltip && !collapsed && (
            <span
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              style={{
                marginLeft: 'auto',
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                background: '#f97316',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                fontWeight: '700',
                cursor: 'pointer',
                flexShrink: 0,
                position: 'relative',
              }}
            >
              ?
              {showTooltip && (
                <div
                  style={{
                    position: 'absolute',
                    right: '0',
                    top: '100%',
                    marginTop: '10px',
                    zIndex: 999,
                    background: '#1e293b',
                    color: '#fff',
                    padding: '8px 14px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    pointerEvents: 'none',
                  }}
                >
                  {/* Arrow */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '-6px',
                      right: '10px',
                      width: 0,
                      height: 0,
                      borderLeft: '6px solid transparent',
                      borderRight: '6px solid transparent',
                      borderBottom: '6px solid #1e293b',
                    }}
                  />
                  {tooltip}
                </div>
              )}
            </span>
          )}
        </Link>
      </div>
    );
  };

  const Label = ({ text }) => (
    <div
      style={{
        fontSize: '10px',
        fontWeight: '600',
        color: '#3f3f3f',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        padding: '16px 14px 6px',
        textAlign: collapsed ? 'center' : 'left',
      }}
    >
      {collapsed ? '•••' : text}
    </div>
  );

  const confirmLogout = () => {
    confirmDialog({
      message: 'Apakah anda yakin ingin keluar?',
      header: 'Konfirmasi Logout',
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Ya, Keluar',
      rejectLabel: 'Batal',
      accept: () => revokeToken(),
    });
  };

  const revokeToken = () => {
    setToken('');
    navigate('/login');
  };

  useEffect(() => {
    const loadDashboardSource = async () => {
      try {
        const data = await fetchMonitoringDashboard();
        setDashboardSource(data);
      } catch (e) {
        console.error('Failed to fetch dashboard source:', e);
      }
    };
    loadDashboardSource();
  }, []);

  useEffect(() => {
    const allowedDashboardIds = getAllowedDashboardIds(user.id);

    if (allowedDashboardIds === undefined || dashboardSource.length === 0) {
      return;
    }

    const savedIndex = Number.parseInt(
      localStorage.getItem('monitoring_active_idx'),
      10
    );
    const savedDashboard = Number.isInteger(savedIndex)
      ? dashboardSource[savedIndex]
      : undefined;

    if (
      savedDashboard &&
      isDashboardAllowed(user.id, savedDashboard.id)
    ) {
      return;
    }

    const firstAllowedIndex = findFirstAllowedDashboardIndex(
      dashboardSource,
      user.id
    );

    if (firstAllowedIndex !== -1) {
      localStorage.setItem(
        'monitoring_active_idx',
        String(firstAllowedIndex)
      );
    } else {
      localStorage.removeItem('monitoring_active_idx');
    }
    window.dispatchEvent(new Event('storage'));
  }, [dashboardSource, user.id]);

  // Access control: blok fitur ini HANYA untuk dimas (via NIK)
  const allowedDimas =
    String(user.nik ?? user.NIK ?? user.Nik ?? '') === AUTHORIZED_NIK;
  
  const allowedUser = [5, 11].includes(user.id);
  const allowedBagian = [2].includes(user.id_bagian);

  return (
    <aside
      style={{
        width: collapsed ? `${COLLAPSED_WIDTH}px` : `${EXPANDED_WIDTH}px`,
        height: '100vh',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: '#edf0f5',
        borderRight: '1px solid #edf0f5',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'width 0.25s ease',
      }}
    >
      <ConfirmDialog />

      {/* Logo */}
      <div
        style={{
          padding: '18px 16px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          background: 'linear-gradient(135deg, #1e0b38, #792f08)',
          borderBottom: '0px solid #a94007',
        }}
      >
        <img
          src="https://portal.geloraaksara.co.id/assets/img/image/LogoG.png"
          alt="Logo"
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            objectFit: 'contain',
            flexShrink: 0,
            background: '#fff',
            padding: '2px',
          }}
        />
        {!collapsed && (
          <div style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden' }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#fff' }}>
              GELORA GROUP
            </div>
            <div
              style={{
                fontSize: '9px',
                color: 'rgba(255,255,255,0.8)',
                letterSpacing: '0.5px',
              }}
            >
              Dashboard v2.1
            </div>
          </div>
        )}
      </div>

      <div
        style={{ height: '1px', background: '#f0e6df', margin: '0 14px 4px' }}
      />

      {/* Nav */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px' }}>
        <Label text="Menu" />

        {/* Display All with collapsible sub-menu */}
        <div>
          <div
            onClick={() => {
              if (!collapsed) setDisplayAllOpen((prev) => !prev);
              navigate('/');
              onClose?.();
            }}
            title={collapsed ? 'Display All' : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '9px 14px',
              marginBottom: '1px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: isActive('/') ? '600' : '400',
              color: isActive('/') ? '#a94007' : '#3f3f3f',
              background: isActive('/') ? '#fff7ed' : 'transparent',
              borderLeft: isActive('/')
                ? '3px solid #a94007'
                : '3px solid transparent',
              transition: 'background 0.15s',
              justifyContent: collapsed ? 'center' : 'flex-start',
            }}
            onMouseEnter={(e) => {
              if (!isActive('/')) e.currentTarget.style.background = '#fff7ed';
            }}
            onMouseLeave={(e) => {
              if (!isActive('/'))
                e.currentTarget.style.background = 'transparent';
            }}
          >
            <i
              className="pi pi-th-large"
              style={{
                fontSize: '14px',
                width: '18px',
                textAlign: 'center',
                opacity: isActive('/') ? 1 : 0.6,
                color: isActive('/') ? '#f97316' : '#000000',
              }}
            />
            {!collapsed && <span style={{ flex: 1 }}>Display All</span>}
            {!collapsed && (
              <ChevronDown
                size={14}
                style={{
                  transition: 'transform 0.2s',
                  transform: displayAllOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  opacity: 0.5,
                }}
              />
            )}
          </div>

          {/* Sub-menu items from dashboard.json */}
          {!collapsed && (
            <div
              style={{
                overflow: 'hidden',
                maxHeight: displayAllOpen
                  ? `${dashboardSource.length * 36}px`
                  : '0px',
                transition: 'max-height 0.25s ease',
              }}
            >
              {dashboardSource.map((dash, index) => {
                const savedIdx = localStorage.getItem('monitoring_active_idx');
                const isSubActive =
                  isActive('/') &&
                  savedIdx !== null &&
                  parseInt(savedIdx, 10) === index;

                if (!isDashboardAllowed(user.id, dash.id)) {
                  return null;
                }

                return (
                  <div
                    key={dash.id}
                    onClick={() => {
                      localStorage.setItem('monitoring_active_idx', index);
                      navigate('/');
                      if (location.pathname === '/') {
                        window.dispatchEvent(new Event('storage'));
                      }
                      onClose?.();
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '7px 14px 7px 42px',
                      marginBottom: '1px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: isSubActive ? '600' : '400',
                      color: isSubActive ? '#a94007' : '#3f3f3f',
                      background: isSubActive ? '#fff7ed' : 'transparent',
                      transition: 'background 0.15s, color 0.15s',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubActive) {
                        e.currentTarget.style.background = '#fff7ed';
                        e.currentTarget.style.color = '#ea580c';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSubActive) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#000000';
                      }
                    }}
                    title={dash.desc}
                  >
                    <i
                      className="pi pi-circle-fill"
                      style={{ fontSize: '5px' }}
                    />
                    <span
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {dash.desc}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {(allowedUser || allowedBagian || allowedDimas) && (
          <div>
            <NavItem
              icon="pi pi-chart-line"
              label="Executive Dashboard"
              to="/executive"
              tooltip="On Development"
            />
            <NavItem
              icon="pi pi-book"
              label="Project Report"
              to="/project-report"
              tooltip=""
            />
          </div>
        )}
        {/* Blok fitur ini hanya untuk dimas */}
        {allowedDimas && (
          <div>
            <NavItem
              icon="pi pi-truck"
              label="Total Logistik & Stock"
              to="/logistic-stock"
              tooltip="On Development"
            />
            <NavItem
              icon="pi pi-box"
              label="Production Dashboard"
              to="/production"
              tooltip="Target Produksi Nasional"
            />
            <NavItem
              icon="pi pi-desktop"
              label="Job Produksi Digital"
              to="/digital-job"
              tooltip="Job toko digital & cek nomor struk"
            />
            <NavItem
              icon="pi pi-receipt"
              label="Rekap Per Struk"
              to="/digital-struk"
              tooltip="Rekap per nomor struk & pelunasan"
            />
          </div>
        )}
      </div>

      {/* User */}
      <div style={{ padding: '12px 14px', borderTop: '1px solid #f0e6df' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}
        >
          <div
            style={{
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #1e0b38, #792f08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: '11px',
              fontWeight: '700',
              flexShrink: 0,
            }}
          >
            {user.nama_lengkap
              ? (() => {
                  const parts = user.nama_lengkap.trim().split(/\s+/);
                  const first = parts[0]?.charAt(0) || '';
                  const last = parts.length > 1 ? parts[parts.length - 1]?.charAt(0) : '';
                  return (first + last).toUpperCase();
                })()
              : '??'}
          </div>
          {!collapsed && (
            <>
              <div>
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#1e293b',
                  }}
                >
                  {user.nama_lengkap}
                </div>
              </div>
              <LogOut
                size={16}
                style={{
                  marginLeft: 'auto',
                  cursor: 'pointer',
                  color: '#64748b',
                  transition: 'color 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#ef4444')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#64748b')}
                onClick={confirmLogout}
              />
            </>
          )}
          {collapsed && (
            <LogOut
              size={16}
              style={{
                position: 'absolute',
                bottom: '48px',
                left: '50%',
                transform: 'translateX(-50%)',
                cursor: 'pointer',
                color: '#64748b',
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#ef4444')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#64748b')}
              onClick={confirmLogout}
            />
          )}
        </div>
      </div>
    </aside>
  );
};

export { EXPANDED_WIDTH, COLLAPSED_WIDTH };
export default Sidebar;
